import { CheckCircle2, Clock, AlertTriangle, SkipForward, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/layout/card';
import type { StepStatus } from '@/types/coachProgress';

export interface PlannerReviewStep {
  id: string;
  label: string;
  status: StepStatus;
}

interface PlannerReviewCardProps {
  title: string;
  steps: PlannerReviewStep[];
  className?: string;
}

function getStatusIcon(status: StepStatus) {
  switch (status) {
    case 'in_progress':
      return <Clock className="h-4 w-4 text-muted-foreground" />;
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-load-fresh" />;
    case 'failed':
      return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case 'skipped':
      return <SkipForward className="h-4 w-4 text-muted-foreground" />;
    case 'planned':
    default:
      return <Square className="h-4 w-4 text-muted-foreground/50" />;
  }
}

function getStatusLabel(status: StepStatus): string {
  switch (status) {
    case 'in_progress':
      return 'in progress';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    case 'planned':
    default:
      return 'planned';
  }
}

export function PlannerReviewCard({ title, steps, className }: PlannerReviewCardProps) {
  return (
    <Card className={cn('bg-accent/5 border-accent/20', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-2" role="list">
          {steps.map((step) => (
            <li
              key={step.id}
              className={cn(
                'flex items-center gap-3 text-sm',
                step.status === 'in_progress' && 'font-medium'
              )}
              aria-busy={step.status === 'in_progress'}
            >
              <span className="flex shrink-0 items-center justify-center" aria-hidden="true">
                {getStatusIcon(step.status)}
              </span>
              <span className="text-foreground">
                {step.label}
                <span className="sr-only"> — {getStatusLabel(step.status)}</span>
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
